import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_ALL_SUPPLIERS_ADMIN, GET_METRICS,
  UPDATE_SUPPLIER_STATUS, UPDATE_SUPPLIER_PERMISSIONS, DELETE_SUPPLIER,
  TEST_EMAIL_ALERT, SIMULATE_PROVIDER_WEBHOOK_OUTCOME
} from '../graphql/operations';
import { useAuth } from '../AuthContext';
import { useToast } from '../components/Toast';

export default function Admin() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [testEmail, setTestEmail] = useState('aphelelesesethu719@gmail.com');
  const [sendingTest, setSendingTest] = useState(false);

  const { data: supData, loading, refetch } = useQuery(GET_ALL_SUPPLIERS_ADMIN);
  const { data: metData } = useQuery(GET_METRICS);

  const [updateStatus] = useMutation(UPDATE_SUPPLIER_STATUS);
  const [updatePerms] = useMutation(UPDATE_SUPPLIER_PERMISSIONS);
  const [deleteSupplier] = useMutation(DELETE_SUPPLIER);
  const [triggerTestEmail] = useMutation(TEST_EMAIL_ALERT);
  const [simulateWebhook] = useMutation(SIMULATE_PROVIDER_WEBHOOK_OUTCOME);

  if (!user || user.role !== 'admin') {
    return <div className="page-container"><div className="empty-state"><i className="fas fa-lock"></i><p>Admin access only.</p></div></div>;
  }

  const suppliers = supData?.suppliers || [];
  const metrics = metData?.metrics;
  const active = suppliers.filter(s => s.status === 'active').length;
  const pending = suppliers.filter(s => s.status === 'pending').length;

  const handleTestEmail = async (e) => {
    e.preventDefault();
    if (!testEmail) return;
    setSendingTest(true);
    try {
      const res = await triggerTestEmail({ variables: { recipient: testEmail.trim() } });
      if (res.data?.testEmailAlert) {
        toast(`turboSMTP test email dispatched to ${testEmail}!`, 'success');
      } else {
        toast('Failed to deliver test email. Check server logs.', 'error');
      }
    } catch (err) {
      toast(err.message || 'Error sending test email', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await updateStatus({ variables: { id, status } });
      toast(`Status updated to ${status}`);
      refetch();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handlePremium = async (id, isPremium) => {
    try {
      await updatePerms({ variables: { id, isPremium } });
      toast(isPremium ? 'Premium granted ⭐' : 'Premium removed');
      refetch();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete supplier and all their products?')) return;
    try {
      await deleteSupplier({ variables: { id } });
      toast('Supplier deleted', 'error');
      refetch();
    } catch (e) { toast(e.message, 'error'); }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">👑 Admin Panel</h1>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-number" style={{ color: '#3b82f6' }}>{suppliers.length}</div>
          <div className="stat-label">Total Suppliers</div>
          <div className="stat-sub">{active} active</div>
        </div>
        <div className="stat-card yellow">
          <div className="stat-number" style={{ color: 'var(--yellow)' }}>{pending}</div>
          <div className="stat-label">Pending Approval</div>
        </div>
        <div className="stat-card green">
          <div className="stat-number" style={{ color: '#22c55e' }}>{metrics?.totalQuotes || 0}</div>
          <div className="stat-label">Quote Requests</div>
        </div>
        <div className="stat-card purple">
          <div className="stat-number" style={{ color: '#a855f7' }}>{metrics?.totalMessages || 0}</div>
          <div className="stat-label">Messages Sent</div>
        </div>
      </div>

      {/* Suppliers Table */}
      <div className="card-section">
        <div className="card-section-title">
          <span>🔐 Supplier Management</span>
        </div>
        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading suppliers...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Location</th>
                  <th>Products</th>
                  <th>External Verification</th>
                  <th>Listing Status</th>
                  <th>Premium</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <img src={s.logo || 'https://picsum.photos/40/40'} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
                          onError={e => { e.target.src = 'https://picsum.photos/40/40'; }} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{s.companyName}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{s.location}</td>
                    <td style={{ textAlign: 'center' }}>{s.productCount}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div>
                          {s.verificationStatus === 'VERIFIED' ? (
                            <span style={{ background: 'rgba(34,197,94,0.18)', color: '#22c55e', border: '1px solid #22c55e', fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px', borderRadius: 3 }}>
                              ✓ VERIFIED
                            </span>
                          ) : s.verificationStatus === 'VERIFICATION_PENDING' ? (
                            <span style={{ background: 'rgba(234,179,8,0.18)', color: 'var(--yellow)', border: '1px solid var(--yellow)', fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px', borderRadius: 3 }}>
                              ⏳ PENDING
                            </span>
                          ) : (
                            <span style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-dim)', fontSize: '0.7rem', padding: '2px 6px', borderRadius: 3 }}>
                              {s.verificationStatus || 'UNVERIFIED'}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                          {s.verificationProvider ? s.verificationProvider.split('/')[0].trim() : 'No Provider'}
                        </div>
                        {/* Admin Webhook Simulator */}
                        <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                          <button
                            title="Simulate external webhook VERIFIED status"
                            style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: 'none', borderRadius: 2, fontSize: '0.65rem', padding: '1px 5px', cursor: 'pointer' }}
                            onClick={async () => {
                              try {
                                await simulateWebhook({ variables: { supplierId: s.id, providerId: 'prov_pbverify', outcomeStatus: 'VERIFIED' } });
                                toast(`Simulated VERIFIED webhook for ${s.companyName}`);
                                refetch();
                              } catch (err) { toast(err.message, 'error'); }
                            }}
                          >
                            Pass
                          </button>
                          <button
                            title="Simulate external webhook FAILED status"
                            style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: 'none', borderRadius: 2, fontSize: '0.65rem', padding: '1px 5px', cursor: 'pointer' }}
                            onClick={async () => {
                              try {
                                await simulateWebhook({ variables: { supplierId: s.id, providerId: 'prov_pbverify', outcomeStatus: 'VERIFICATION_FAILED' } });
                                toast(`Simulated FAILED webhook for ${s.companyName}`);
                                refetch();
                              } catch (err) { toast(err.message, 'error'); }
                            }}
                          >
                            Fail
                          </button>
                        </div>
                      </div>
                    </td>
                    <td>
                      <select
                        className="input" style={{ width: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.78rem' }}
                        value={s.status}
                        onChange={e => handleStatus(s.id, e.target.value)}
                      >
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </td>
                    <td>
                      {s.isPremium ? (
                        <button style={{ background: 'none', color: 'var(--yellow)', fontSize: '0.8rem', fontWeight: 700 }}
                          onClick={() => handlePremium(s.id, false)}>⭐ Premium</button>
                      ) : (
                        <button style={{ background: 'none', color: 'var(--text-dim)', fontSize: '0.78rem' }}
                          onClick={() => handlePremium(s.id, true)}>Grant ⭐</button>
                      )}
                    </td>
                    <td>
                      <button style={{ background: 'none', color: 'var(--error)', fontSize: '0.82rem', marginLeft: '0.5rem' }}
                        onClick={() => handleDelete(s.id)}>
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Metrics */}
      <div className="card-section">
        <div className="card-section-title">📊 Site Metrics</div>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.875rem' }}>
          <MetricItem icon="fa-eye" label="Total Visits" value={metrics?.totalVisits || 0} />
          <MetricItem icon="fa-envelope" label="Quote Requests" value={metrics?.totalQuotes || 0} />
          <MetricItem icon="fa-comment" label="Messages" value={metrics?.totalMessages || 0} />
        </div>
      </div>

      {/* turboSMTP Gateway Integration Panel */}
      <div className="card-section" style={{ border: '1px solid #4a3f10', background: 'linear-gradient(135deg, #181816 0%, #201e10 100%)' }}>
        <div className="card-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ background: 'var(--yellow)', color: '#000', padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: '0.75rem' }}>ACTIVE</span>
            ⚡ turboSMTP Automated Email Gateway
          </span>
          <span style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: 600 }}>
            <i className="fas fa-shield-alt" style={{ marginRight: 4 }}></i> SMTP Authentication Enabled (Port 465 SSL)
          </span>
        </div>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          All quote requests (RFQs), buyer confirmation receipts, and contact inquiries are authenticated and routed through turboSMTP from <strong style={{ color: 'var(--yellow)' }}>aphelelesesethu719@gmail.com</strong> with consumer key <code style={{ color: 'var(--yellow)', background: '#292524', padding: '2px 6px', borderRadius: 4 }}>a0d87886...</code> and bounce-prevention headers.
        </p>
        <form onSubmit={handleTestEmail} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="email"
            className="input"
            style={{ maxWidth: 320, flex: 1 }}
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="Recipient email address"
            required
          />
          <button type="submit" className="btn-yellow" disabled={sendingTest}>
            {sendingTest ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i> Dispatching...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane" style={{ marginRight: 6 }}></i> Send Test turboSMTP Alert
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function MetricItem({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--bg3)', padding: '0.75rem 1.25rem', borderRadius: 8, border: '1px solid var(--border)' }}>
      <i className={`fas ${icon}`} style={{ color: 'var(--yellow)', fontSize: '1.2rem' }}></i>
      <div>
        <div style={{ fontSize: '1.25rem', fontWeight: 800 }}>{value.toLocaleString()}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{label}</div>
      </div>
    </div>
  );
}
